import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowLeft,
    MessageCircleHeart,
    ChevronRight,
    Heart
} from 'lucide-react';
import { TertiaryButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import Container from '../components/brand/BrandLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Repair & Reconnection
 * 
 * Questions to guide important conversations, 
 * help navigate emotional topics, and achieve repair.
 * 
 * Content is loaded from the database and editable via Tool Editor.
 */

const RepairReconnection = () => {
    const navigate = useNavigate();
    const [content, setContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSection, setExpandedSection] = useState(null);

    useEffect(() => {
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/content/repair-reconnection`);
                if (response.ok) {
                    const data = await response.json();
                    setContent(data);
                }
            } catch (error) {
                console.error('Failed to fetch repair content:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    // Default content if none exists in database
    const defaultContent = {
        title: 'Repair & Reconnection',
        subtitle: 'Questions to guide important conversations and achieve repair',
        intro: "These prompting questions can help you navigate difficult conversations, stay grounded during emotional discussions, and work toward reconnection.",
        sections: []
    };

    const displayContent = content || defaultContent;

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-3 h-3 rounded-full bg-[#FFBB98] animate-pulse" />
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            {/* Header */}
            <header className="sticky top-0 z-40 glass-header">
                <Container size="lg" className="py-4">
                    <div className="flex items-center justify-between">
                        <button 
                            onClick={() => navigate('/relationship-hub')}
                            className="p-2 -ml-2 rounded-full hover:bg-[#FFBB98]/40 transition-colors text-[#7D8E95] hover:text-[#1D6B6B]"
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <span className="font-medium text-[#1D6B6B]">Repair & Reconnection</span>
                        <div className="w-11" />
                    </div>
                </Container>
            </header>

            <main className="max-w-2xl mx-auto px-6 py-12">
                {/* Hero */}
                <div className="text-center mb-12">
                    <IconContainer variant="rose" size="xl" useGradient={true} className="mx-auto mb-6">
                        <MessageCircleHeart className="w-8 h-8" />
                    </IconContainer>
                    <Heading as="h1" size="text-2xl md:text-3xl" className="mb-3">
                        {displayContent.title}
                    </Heading>
                    <BodyText className="text-[#7D8E95] max-w-lg mx-auto">
                        {displayContent.subtitle}
                    </BodyText>
                </div>

                {/* Intro */}
                {displayContent.intro && (
                    <div className="glass-card rounded-2xl p-5">
                        <BodyText className="text-[#2A8585] leading-relaxed text-center">
                            {displayContent.intro}
                        </BodyText>
                    </div>
                )}

                {/* Sections with Questions */}
                {displayContent.sections && displayContent.sections.length > 0 ? (
                    <div className="space-y-4">
                        {displayContent.sections.map((section, index) => (
                            <div className="glass-card rounded-2xl p-5"
                                key={section.id || index}
                                variant="elevated" 
                                padding="none"
                                className="overflow-hidden"
                            >
                                <button
                                    onClick={() => setExpandedSection(expandedSection === index ? null : index)}
                                    className="w-full p-5 flex items-center justify-between text-left hover:bg-[#FFBB98]/10 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className="w-10 h-10 rounded-full bg-[#FFBB98]/30 flex items-center justify-center">
                                            <Heart className="w-5 h-5 text-[#FFBB98]" />
                                        </div>
                                        <div>
                                            <Heading as="h3" size="text-lg" className="text-[#1D6B6B]">
                                                {section.title}
                                            </Heading>
                                            {section.description && (
                                                <SmallText className="text-[#7D8E95]">
                                                    {section.description}
                                                </SmallText>
                                            )}
                                        </div>
                                    </div>
                                    <ChevronRight 
                                        className={`w-5 h-5 text-[#7D8E95] transition-transform ${
                                            expandedSection === index ? 'rotate-90' : ''
                                        }`} 
                                    />
                                </button>
                                
                                {expandedSection === index && section.questions && (
                                    <div className="px-5 pb-5 pt-2 border-t border-white/40/40">
                                        <div className="space-y-4">
                                            {section.questions.map((question, qIndex) => (
                                                <div 
                                                    key={qIndex}
                                                    className="pl-4 border-l-2 border-white/40/50"
                                                >
                                                    <BodyText className="text-[#1D6B6B] leading-relaxed">
                                                        {question}
                                                    </BodyText>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="glass-card rounded-2xl p-5">
                        <Heart className="w-12 h-12 text-[#FFBB98] mx-auto mb-4" />
                        <Heading as="h3" size="text-lg" className="text-[#7D8E95] mb-2">
                            Coming Soon
                        </Heading>
                        <BodyText className="text-[#7D8E95]">
                            Questions and guides for repair conversations are being prepared.
                        </BodyText>
                    </div>
                )}

                {/* Gentle reminder */}
                <div className="mt-12 text-center">
                    <SmallText className="text-[#7D8E95] italic">
                        Remember: It's okay to take breaks. Repair doesn't have to happen all at once.
                    </SmallText>
                </div>
            </main>
        </div>
    );
};

export default RepairReconnection;
